import React, { useState, useCallback, useMemo, useContext } from 'react';
import i18n from 'i18n';
import { SeasonType } from 'store/pages/loaders/loader-types';
import { get, isEmpty, max } from 'lodash';
import scaleSize from 'utils/scale-size';
import { LIST_ITEMS_SIZE } from 'config';
import ListItem, { LIST_ITEM_MARGIN } from 'components/CustomCollection/ListItem';
import withKeepingLastFocus from 'components/HOCs/withKeepingLastFocus';
import { DetailsContext } from './DetailsContext';
import { StyledEpisodesList } from './StyledComponents';
import {
  SeasonsWrapper,
  SeasonsTabs,
  SeasonsTab,
  StyledEmptySeasonStub,
  StyledEmptySeasonStubTitle,
} from './StyledComponents';

const CONFIG = {
  enterTo: 'default-element',
  defaultElement: '.selectedTab',
};

const LIST_CONFIG = {
  leaveFor: {
    right: '',
  },
};

const itemSize = LIST_ITEMS_SIZE.LARGE.width + LIST_ITEM_MARGIN;

const TAB_ITEM_SIZES = [
  // small
  { range: [0, 8], size: 170 },
  // medium
  { range: [9, 16], size: 340 },
];

const isInRange = (v, r) => r[0] <= v && v <= r[1];

const EmptySeasonStub = ({ isLoading }) => {
  return (
    <StyledEmptySeasonStub>
      <StyledEmptySeasonStubTitle>
        {isLoading ? '' : i18n.t('detailsVOD.noEpisodesInSeason')}
      </StyledEmptySeasonStubTitle>
    </StyledEmptySeasonStub>
  );
};

interface ISeasons {
  sectionName: string;
  seasons: SeasonType[];
  onFocus: () => void;
  onListItemEnter: () => void;
  lastFocusKeeper;
}

const Seasons: React.FC<ISeasons> = props => {
  const { sectionName, seasons } = props;
  const { onFocus, onListItemEnter, lastFocusKeeper } = props;
  const details = useContext(DetailsContext);
  // This is inner "last-focus" key. It's unique by vod item id
  const LAST_FOCUS_KEY = `DETAILS_SEASONS_${details.id}`;

  const [selectedSeasonIndex, setSelectedSeasonIndex] = useState(() => {
    return get(lastFocusKeeper.getSaved(LAST_FOCUS_KEY), ['index'], 0);
  });

  const isSelectedSeasonHasEpisodes = !isEmpty(seasons[selectedSeasonIndex]);

  const onTabEnter = useCallback(
    (_item, index) => {
      setSelectedSeasonIndex(index);
      lastFocusKeeper.save(LAST_FOCUS_KEY, { index });
    },
    [LAST_FOCUS_KEY, lastFocusKeeper],
  );

  const tabItemWidth = useMemo(() => {
    const titles = seasons.map(item => item.name);
    const maxLength = max(titles);
    // small by default
    let width = TAB_ITEM_SIZES[0].size;

    for (const { range, size } of TAB_ITEM_SIZES) if (isInRange(maxLength, range)) width = size;

    return width;
  }, [seasons]);

  const tabs = useMemo(() => {
    return (
      <SeasonsTabs
        sectionName={sectionName}
        config={CONFIG}
        listId="tabs-list"
        itemSize={scaleSize(tabItemWidth)}
        startScrollIndex={3}
        items={seasons}
        onFocus={onFocus}
        onEnter={onTabEnter}
        itemRender={({ item, index, ...rest }) => {
          const isSelected = index === selectedSeasonIndex;
          return (
            <SeasonsTab
              id={item.id}
              isSelected={isSelected}
              className={isSelected && 'selectedTab'}
              payload={index}
              index={index}
              itemWidth={scaleSize(tabItemWidth)}
              {...rest}
            >
              {item.name}
            </SeasonsTab>
          );
        }}
      />
    );
  }, [onFocus, onTabEnter, seasons, sectionName, selectedSeasonIndex, tabItemWidth]);

  const content = useMemo(() => {
    if (!isSelectedSeasonHasEpisodes) return <EmptySeasonStub isLoading={false} />;

    return (
      <StyledEpisodesList
        listId={'episodes-list'}
        sectionName={'EPISODES-LIST'}
        startScrollIndex={1}
        itemSize={itemSize}
        items={seasons[selectedSeasonIndex].episodes}
        onEnter={onListItemEnter}
        onFocus={onFocus}
        config={LIST_CONFIG}
        renderSize={10}
        itemComponent={ListItem.EPISODE}
      />
    );
  }, [isSelectedSeasonHasEpisodes, onFocus, onListItemEnter, seasons, selectedSeasonIndex]);

  return (
    <SeasonsWrapper>
      {tabs}
      {content}
    </SeasonsWrapper>
  );
};

export default withKeepingLastFocus(Seasons);
